import axios from 'axios';
import {useScrollViewVisibility} from 'components/Context/ScrollView';
import {cacheHit, useCache} from 'hooks/cache';
import {RefObject, useEffect, useState} from 'react';
import {useLocale} from 'state/locale';
import {requestHeaders} from 'utils/request-axios';
import {z} from 'zod';

import {intToCachedPDFImageWidth} from './utils';

const useComputedBounds = (reference: RefObject<HTMLElement>): DOMRect => {
    const [bounds, setBounds] = useState<DOMRect>(new DOMRect());
    useEffect(() => {
        const interval = setInterval(() => {
            if (reference.current) {
                const rect = reference.current.getBoundingClientRect();
                if (rect.width && rect.height) {
                    if (
                        rect.width !== bounds.width ||
                        rect.height !== bounds.height
                    ) {
                        setBounds(rect);
                    }
                }
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [reference, bounds]);

    return bounds;
};

export const useCachedImageWidth = (
    reference: RefObject<HTMLImageElement>,
): number => {
    const rect = useComputedBounds(reference);
    return intToCachedPDFImageWidth(rect.width);
};

export const useLazyLoadedImage = (
    url: string,
    shouldRender: boolean,
):
    | {
          data: null;
          error: Error;
          isLoading: false;
      }
    | {
          data: null;
          error: null;
          isLoading: true;
      }
    | {
          data: string;
          error: null;
          isLoading: false;
      } => {
    const cache = useCache();
    const locale = useLocale();
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<null | string>(null);
    useEffect(() => {
        if (!shouldRender) return;
        setIsLoading(true);
        cache
            .fn(url, () =>
                axios
                    .get(url, {
                        headers: requestHeaders({locale}),
                        responseType: 'blob',
                    })
                    .then((response) => URL.createObjectURL(response.data))
                    .catch(setError),
            )
            .then((value) => {
                setData(z.string().parse(value));
                setIsLoading(false);
            });

        return () => {
            if (!data) return;
            URL.revokeObjectURL(data);
        };
    }, [url, shouldRender]);

    if (isLoading) {
        return {data: null, error: null, isLoading: true};
    }
    if (data && !error) {
        return {data, error, isLoading: false};
    }
    if (error) {
        return {data: null, error, isLoading: false};
    }
    throw new Error('Image data is null');
};

const DEFAULT_DELAY = 300;

/**
 * This function determine whether of not the image is 'ready to be rendered'.
 * Conditions that affect this are:
 * - If the page number is less than 10, it will be rendered immediately.
 * - If the image is in the cache, it will be rendered immediately.
 * - If the image is not in the cache, it will be rendered after DEFAULT_DELAY timeout
 */
export const useReadyToRender = (
    ref: RefObject<HTMLElement>,
    options: {
        key: string;
        page: number;
    },
): boolean => {
    const {isIntersecting} = useScrollViewVisibility(ref);
    const [shouldRender, setShouldRender] = useState(false);
    useEffect(() => {
        if (shouldRender) return;
        if (cacheHit(options.key)) {
            setShouldRender(true);
            return;
        }
        if (options.page < 10) {
            setShouldRender(true);
            return;
        }
        if (!isIntersecting) return;
        const timer = setTimeout(() => {
            setShouldRender(true);
        }, DEFAULT_DELAY);
        return () => {
            clearTimeout(timer);
        };
    }, [isIntersecting, options.key]);
    return shouldRender;
};
